import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

const formatDate = (iso) =>
  new Date(iso).toLocaleString("fr-BE", {
    dateStyle: "short",
    timeStyle: "short",
  });

const STATUS_STYLES = {
  sandbox: "border-[#E8DCC8]/40 bg-[#E8DCC8]/10 text-[#E8DCC8]",
  sent: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  failed: "border-red-500/40 bg-red-500/10 text-red-300",
};

export default function EmailLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (templateFilter !== "all") params.set("template_key", templateFilter);
      const { data } = await api.get(`/email-logs?${params.toString()}`);
      setLogs(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
     
  }, [statusFilter, templateFilter]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/email-logs/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const templates = Array.from(new Set(logs.map((l) => l.template_key)));

  return (
    <div className="space-y-6" data-testid="email-log-page">
      <div>
        <h1 className="text-2xl font-bold">Journal des emails</h1>
        <p className="text-sm text-slate-400">
          Tous les emails (test et réels) envoyés depuis votre entreprise.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 border-slate-700 bg-slate-950/40" data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-slate-700 bg-slate-900">
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="sandbox">Mode test</SelectItem>
            <SelectItem value="sent">Envoyé</SelectItem>
            <SelectItem value="failed">Échec</SelectItem>
          </SelectContent>
        </Select>
        <Select value={templateFilter} onValueChange={setTemplateFilter}>
          <SelectTrigger className="w-48 border-slate-700 bg-slate-950/40" data-testid="filter-template">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-slate-700 bg-slate-900">
            <SelectItem value="all">Tous templates</SelectItem>
            {templates.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="overflow-x-auto p-0">
          {loading ? (
            <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : logs.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">Aucun email pour le moment.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-800 text-slate-400">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Template</th>
                  <th className="p-3 text-left">Destinataire</th>
                  <th className="p-3 text-left">Sujet</th>
                  <th className="p-3 text-left">Statut</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => openDetail(l.id)}
                    className="cursor-pointer border-b border-slate-800/60 last:border-0 hover:bg-slate-800/40"
                    data-testid={`log-row-${l.id}`}
                  >
                    <td className="p-3 font-mono text-xs text-slate-500">
                      {formatDate(l.created_at)}
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className="border-slate-600 bg-slate-800 text-xs">
                        {l.template_key}
                      </Badge>
                    </td>
                    <td className="p-3 text-slate-300">{l.to}</td>
                    <td className="p-3 max-w-xs truncate text-slate-400">{l.subject}</td>
                    <td className="p-3">
                      <Badge className={`border ${STATUS_STYLES[l.status] || ""}`}>
                        {l.status === "sandbox" ? "Mode test" : l.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl border-slate-800 bg-slate-900 text-slate-100" data-testid="log-detail-modal">
          <DialogHeader>
            <DialogTitle>{detail?.subject}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 rounded border border-slate-700 bg-slate-950/40 p-3 text-xs">
                <div>
                  <span className="text-slate-500">De :</span> {detail.from_}
                </div>
                <div>
                  <span className="text-slate-500">À :</span> {detail.to}
                </div>
                <div>
                  <span className="text-slate-500">Template :</span> {detail.template_key}
                </div>
                <div>
                  <span className="text-slate-500">Statut :</span> {detail.status === "sandbox" ? "Mode test" : detail.status}
                </div>
                <div>
                  <span className="text-slate-500">Envoyé :</span> {formatDate(detail.created_at)}
                </div>
                {detail.error && (
                  <div className="col-span-2 text-red-300">
                    <span className="text-slate-500">Erreur :</span> {detail.error}
                  </div>
                )}
              </div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Aperçu HTML</div>
              <iframe
                title="Aperçu email"
                srcDoc={detail.body_html}
                sandbox=""
                className="h-96 w-full rounded border border-slate-700 bg-white"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
